/**
 * Autocomplete cliente su `searchClienti`. Tre consumatori: il filtro della
 * lista uscite, il passo 4 del wizard e la modale di modifica.
 *
 * Non crea clienti, a differenza di `CambiaClienteModal`: chi registra
 * un'uscita sta trascrivendo un FIR già compilato, e inventare un'anagrafica
 * da lì è il modo di ritrovarsi due volte lo stesso destinatario.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { searchClienti } from '../../api/clienti';
import type { Cliente } from '../../types/cliente';
import { useDebounce } from '../../hooks/useDebounce';

interface Props {
  value: { id: string; ragione_sociale: string } | null;
  onChange: (c: { id: string; ragione_sociale: string } | null) => void;
  placeholder: string;
  /** Mostrato sopra il campo; assente nei filtri, dove lo spazio è prezioso. */
  label?: string;
  disabled?: boolean;
  testid?: string;
}

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage bg-white disabled:bg-stone-bg disabled:text-ink-soft';

export default function ClienteSearch({ value, onChange, placeholder, label, disabled, testid }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [suggerimenti, setSuggerimenti] = useState<Cliente[]>([]);
  const [aperto, setAperto] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const debounced = useDebounce(query, 300);

  useEffect(() => {
    void (async () => {
      if (!debounced.trim()) { setSuggerimenti([]); return; }
      try { setSuggerimenti(await searchClienti(debounced, 10)); }
      catch { setSuggerimenti([]); }
    })();
  }, [debounced]);

  useEffect(() => {
    function fuori(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setAperto(false);
    }
    document.addEventListener('mousedown', fuori);
    return () => document.removeEventListener('mousedown', fuori);
  }, []);

  if (value) {
    return (
      <div>
        {label && <label className="block text-xs text-ink-soft mb-1">{label}</label>}
        <div className="flex items-center gap-2 text-sm border border-stone-border rounded px-2 py-1.5 bg-white" data-testid={testid}>
          <span className="flex-1 truncate text-ink-dark">{value.ragione_sociale}</span>
          {!disabled && (
            <button type="button" onClick={() => { onChange(null); setQuery(''); }}
              aria-label={t('common.azioni.annulla')} className="text-ink-soft hover:text-ink-dark">
              <X size={14} />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      {label && <label className="block text-xs text-ink-soft mb-1">{label}</label>}
      <input
        type="text" className={inputCls} placeholder={placeholder} value={query} disabled={disabled}
        data-testid={testid}
        onChange={(e) => { setQuery(e.target.value); setAperto(true); }}
        onFocus={() => setAperto(true)}
      />
      {aperto && suggerimenti.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-stone-mid rounded-lg shadow-lg">
          {suggerimenti.map((c) => (
            <li key={c.id}>
              <button type="button"
                onClick={() => { onChange({ id: c.id, ragione_sociale: c.ragione_sociale }); setAperto(false); setQuery(''); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-stone-bg">
                <span className="text-ink-dark">{c.ragione_sociale}</span>
                {c.piva && <span className="ml-2 text-xs text-ink-faint">{c.piva}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
